import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  House, CalendarCheck, Bed, SignOut, Plus, PencilSimple, Trash,
  ClockCountdown, CheckCircle, Package, ListChecks,
} from "@phosphor-icons/react";
import logoWhite from "../assets/logo-white.png";

const STATUS = {
  in_attesa: { label: "In attesa", cls: "bg-amber-100 text-amber-800" },
  confermata: { label: "Confermata", cls: "bg-blue-100 text-blue-800" },
  completata: { label: "Completata", cls: "bg-emerald-100 text-emerald-800" },
  annullata: { label: "Annullata", cls: "bg-rose-100 text-rose-800" },
};

const emptyBooking = {
  guest_name: "", property_name: "", address: "", phone: "",
  checkin: "", checkout: "", service_type: "entrambi", linen_sets: 0, notes: "", status: "in_attesa",
};
const emptyLinen = { name: "", category: "lenzuola", total: 0, available: 0 };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-muted-foreground block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
const inputCls = "w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-background outline-none focus:border-primary transition-colors font-normal";

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-primary/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-background rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <h3 className="serif text-3xl font-light text-primary mb-6">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [linen, setLinen] = useState([]);

  const [bookingModal, setBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState(emptyBooking);
  const [editingBooking, setEditingBooking] = useState(null);

  const [linenModal, setLinenModal] = useState(false);
  const [linenForm, setLinenForm] = useState(emptyLinen);
  const [editingLinen, setEditingLinen] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, l] = await Promise.all([
        api.get("/stats"), api.get("/bookings"), api.get("/linen"),
      ]);
      setStats(s.data); setBookings(b.data); setLinen(l.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/login");
    }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  // ---- Booking handlers ----
  const openNewBooking = () => { setEditingBooking(null); setBookingForm(emptyBooking); setBookingModal(true); };
  const openEditBooking = (b) => { setEditingBooking(b); setBookingForm(b); setBookingModal(true); };
  const saveBooking = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...bookingForm, linen_sets: Number(bookingForm.linen_sets) || 0 };
      if (editingBooking) await api.put(`/bookings/${editingBooking.id}`, payload);
      else await api.post("/bookings", payload);
      toast.success(editingBooking ? "Prenotazione aggiornata" : "Prenotazione creata");
      setBookingModal(false); loadAll();
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Eliminare questa prenotazione?")) return;
    await api.delete(`/bookings/${id}`); toast.success("Eliminata"); loadAll();
  };
  const quickStatus = async (b, status) => {
    await api.put(`/bookings/${b.id}`, { status }); loadAll();
  };

  // ---- Linen handlers ----
  const openNewLinen = () => { setEditingLinen(null); setLinenForm(emptyLinen); setLinenModal(true); };
  const openEditLinen = (l) => { setEditingLinen(l); setLinenForm(l); setLinenModal(true); };
  const saveLinen = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...linenForm, total: Number(linenForm.total) || 0, available: Number(linenForm.available) || 0 };
      if (editingLinen) await api.put(`/linen/${editingLinen.id}`, payload);
      else await api.post("/linen", payload);
      toast.success(editingLinen ? "Articolo aggiornato" : "Articolo aggiunto");
      setLinenModal(false); loadAll();
    } catch { toast.error("Errore nel salvataggio"); }
  };
  const deleteLinen = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    await api.delete(`/linen/${id}`); toast.success("Eliminato"); loadAll();
  };

  const navItems = [
    { id: "overview", label: "Panoramica", icon: House },
    { id: "bookings", label: "Prenotazioni", icon: CalendarCheck },
    { id: "linen", label: "Biancheria", icon: Bed },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-muted/40 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-primary text-primary-foreground flex-col justify-between hidden md:flex sticky top-0 h-screen p-6">
        <div>
          <img src={logoWhite} alt="Cleansof" className="h-9 w-auto mb-1" />
          <p className="text-primary-foreground/40 text-xs mb-10">Area gestione</p>
          <nav className="space-y-1">
            {navItems.map((n) => (
              <button key={n.id} data-testid={`nav-tab-${n.id}`} onClick={() => setTab(n.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${
                  tab === n.id ? "bg-background/10 text-primary-foreground" : "text-primary-foreground/50 hover:text-primary-foreground"
                }`}>
                <n.icon size={20} weight={tab === n.id ? "fill" : "regular"} /> {n.label}
              </button>
            ))}
          </nav>
        </div>
        <button onClick={doLogout} data-testid="logout-btn"
          className="flex items-center gap-3 px-4 py-3 text-sm text-primary-foreground/50 hover:text-primary-foreground transition-colors">
          <SignOut size={20} /> Esci
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile tabs */}
        <div className="md:hidden flex items-center justify-between bg-primary text-primary-foreground px-5 py-4">
          <span className="serif text-2xl"><img src={logoWhite} alt="Cleansof" className="h-7 w-auto" /></span>
          <button onClick={doLogout}><SignOut size={22} /></button>
        </div>
        <div className="md:hidden flex gap-2 p-4 overflow-x-auto">
          {navItems.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${tab === n.id ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground"}`}>
              {n.label}
            </button>
          ))}
        </div>

        <div className="p-6 lg:p-10 max-w-6xl">
          <div className="mb-8">
            <p className="text-muted-foreground text-sm">Bentornato, {user?.name || "Admin"}</p>
            <h1 className="serif text-4xl font-light text-primary capitalize">{navItems.find((n) => n.id === tab)?.label}</h1>
          </div>

          {/* OVERVIEW */}
          {tab === "overview" && stats && (
            <div data-testid="overview-tab" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { k: "total", label: "Prenotazioni totali", icon: ListChecks },
                { k: "pending", label: "In attesa", icon: ClockCountdown },
                { k: "confirmed", label: "Confermate", icon: CheckCircle },
                { k: "linen_items", label: "Articoli biancheria", icon: Package },
              ].map((c) => (
                <div key={c.k} className="bg-background rounded-2xl border border-border p-6">
                  <c.icon size={28} weight="thin" className="text-secondary mb-6" />
                  <div className="serif text-5xl font-light text-primary">{stats[c.k]}</div>
                  <div className="text-muted-foreground text-sm mt-1">{c.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* BOOKINGS */}
          {tab === "bookings" && (
            <div data-testid="bookings-tab">
              <button onClick={openNewBooking} data-testid="add-booking-btn"
                className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm hover:bg-primary/90 transition-colors">
                <Plus size={18} /> Nuova prenotazione
              </button>
              <div className="bg-background rounded-2xl border border-border overflow-hidden">
                {bookings.length === 0 ? (
                  <p className="p-10 text-center text-muted-foreground">Nessuna prenotazione. Aggiungine una.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {bookings.map((b) => (
                      <div key={b.id} data-testid={`booking-row-${b.id}`} className="p-5 flex flex-wrap items-center gap-4 justify-between hover:bg-muted/30 transition-colors">
                        <div className="min-w-[180px]">
                          <div className="font-medium text-primary">{b.property_name}</div>
                          <div className="text-sm text-muted-foreground">{b.guest_name} · {b.phone || "—"}</div>
                        </div>
                        <div className="text-sm text-muted-foreground">{b.checkin} → {b.checkout}</div>
                        <div className="text-sm capitalize text-muted-foreground">{b.service_type} · {b.linen_sets} set</div>
                        <select value={b.status} onChange={(e) => quickStatus(b, e.target.value)}
                          data-testid={`booking-status-${b.id}`}
                          className={`text-xs font-medium rounded-full px-3 py-1.5 border-0 outline-none cursor-pointer ${STATUS[b.status]?.cls}`}>
                          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                        <div className="flex gap-1">
                          <button onClick={() => openEditBooking(b)} data-testid={`edit-booking-${b.id}`} className="p-2 text-muted-foreground hover:text-primary"><PencilSimple size={18} /></button>
                          <button onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`} className="p-2 text-muted-foreground hover:text-rose-600"><Trash size={18} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* LINEN */}
          {tab === "linen" && (
            <div data-testid="linen-tab">
              <button onClick={openNewLinen} data-testid="add-linen-btn"
                className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm hover:bg-primary/90 transition-colors">
                <Plus size={18} /> Nuovo articolo
              </button>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {linen.length === 0 ? (
                  <p className="col-span-full p-10 text-center text-muted-foreground bg-background rounded-2xl border border-border">Nessun articolo in inventario.</p>
                ) : linen.map((l) => (
                  <div key={l.id} data-testid={`linen-card-${l.id}`} className="bg-background rounded-2xl border border-border p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="overline text-secondary mb-1">{l.category}</div>
                        <div className="serif text-2xl font-light text-primary">{l.name}</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openEditLinen(l)} data-testid={`edit-linen-${l.id}`} className="p-1.5 text-muted-foreground hover:text-primary"><PencilSimple size={16} /></button>
                        <button onClick={() => deleteLinen(l.id)} data-testid={`delete-linen-${l.id}`} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash size={16} /></button>
                      </div>
                    </div>
                    <div className="mt-6 flex items-end gap-4">
                      <div><div className="text-3xl serif font-light text-primary">{l.available}</div><div className="text-xs text-muted-foreground">disponibili</div></div>
                      <div className="text-muted-foreground">/ {l.total} totali</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      <Modal open={bookingModal} onClose={() => setBookingModal(false)} title={editingBooking ? "Modifica prenotazione" : "Nuova prenotazione"}>
        <form onSubmit={saveBooking} className="space-y-4" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nome ospite"><input required className={inputCls} value={bookingForm.guest_name} onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })} data-testid="bf-guest" /></Field>
            <Field label="Immobile"><input required className={inputCls} value={bookingForm.property_name} onChange={(e) => setBookingForm({ ...bookingForm, property_name: e.target.value })} data-testid="bf-property" /></Field>
          </div>
          <Field label="Indirizzo"><input className={inputCls} value={bookingForm.address} onChange={(e) => setBookingForm({ ...bookingForm, address: e.target.value })} data-testid="bf-address" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Telefono"><input className={inputCls} value={bookingForm.phone} onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })} data-testid="bf-phone" /></Field>
            <Field label="Set biancheria"><input type="number" min="0" className={inputCls} value={bookingForm.linen_sets} onChange={(e) => setBookingForm({ ...bookingForm, linen_sets: e.target.value })} data-testid="bf-linen" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Check-in"><input type="date" required className={inputCls} value={bookingForm.checkin} onChange={(e) => setBookingForm({ ...bookingForm, checkin: e.target.value })} data-testid="bf-checkin" /></Field>
            <Field label="Check-out"><input type="date" required className={inputCls} value={bookingForm.checkout} onChange={(e) => setBookingForm({ ...bookingForm, checkout: e.target.value })} data-testid="bf-checkout" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Servizio"><select className={inputCls} value={bookingForm.service_type} onChange={(e) => setBookingForm({ ...bookingForm, service_type: e.target.value })} data-testid="bf-service">
              <option value="pulizie">Pulizie</option><option value="biancheria">Biancheria</option><option value="entrambi">Entrambi</option>
            </select></Field>
            <Field label="Stato"><select className={inputCls} value={bookingForm.status} onChange={(e) => setBookingForm({ ...bookingForm, status: e.target.value })} data-testid="bf-status">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></Field>
          </div>
          <Field label="Note"><textarea className={inputCls} rows="2" value={bookingForm.notes} onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })} data-testid="bf-notes" /></Field>
          <div className="flex gap-3 pt-2">
            <button type="submit" data-testid="save-booking-btn" className="flex-1 rounded-full bg-primary text-primary-foreground py-3 hover:bg-primary/90 transition-colors">Salva</button>
            <button type="button" onClick={() => setBookingModal(false)} className="rounded-full border border-border px-6 py-3 hover:bg-muted transition-colors">Annulla</button>
          </div>
        </form>
      </Modal>

      {/* Linen Modal */}
      <Modal open={linenModal} onClose={() => setLinenModal(false)} title={editingLinen ? "Modifica articolo" : "Nuovo articolo"}>
        <form onSubmit={saveLinen} className="space-y-4" data-testid="linen-form">
          <Field label="Nome"><input required className={inputCls} value={linenForm.name} onChange={(e) => setLinenForm({ ...linenForm, name: e.target.value })} data-testid="lf-name" /></Field>
          <Field label="Categoria"><select className={inputCls} value={linenForm.category} onChange={(e) => setLinenForm({ ...linenForm, category: e.target.value })} data-testid="lf-category">
            <option value="lenzuola">Lenzuola</option><option value="asciugamani">Asciugamani</option><option value="federe">Federe</option><option value="altro">Altro</option>
          </select></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Totale"><input type="number" min="0" className={inputCls} value={linenForm.total} onChange={(e) => setLinenForm({ ...linenForm, total: e.target.value })} data-testid="lf-total" /></Field>
            <Field label="Disponibili"><input type="number" min="0" className={inputCls} value={linenForm.available} onChange={(e) => setLinenForm({ ...linenForm, available: e.target.value })} data-testid="lf-available" /></Field>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" data-testid="save-linen-btn" className="flex-1 rounded-full bg-primary text-primary-foreground py-3 hover:bg-primary/90 transition-colors">Salva</button>
            <button type="button" onClick={() => setLinenModal(false)} className="rounded-full border border-border px-6 py-3 hover:bg-muted transition-colors">Annulla</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
